import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAuditLogs } from "../../api/access";
import "../../css/AccessManagement.css";
import { formatDateTime, getAccessErrorMessage } from "../../utils/accessErrors";
import Layout from "../Layout";
import { adminLabel, useAdminList } from "./accessData";

const PAGE_SIZE = 20;

const ACTION_LABEL = {
  OVERRIDE_SET: "개인 예외 설정",
  OVERRIDE_CLEAR: "개인 예외 해제(역할 따름)",
  USER_ROLES_REPLACE: "역할 배정 변경",
  ROLE_CREATE: "역할 생성",
  ROLE_UPDATE: "역할 수정",
  ROLE_DELETE: "역할 삭제",
  ROLE_PERMISSIONS_REPLACE: "역할 권한 변경",
};

const EMPTY_FILTER = { targetType: "", targetId: "", actorAdminUserId: "", from: "", to: "" };

// 날짜(YYYY-MM-DD) → ISO. to 는 그날 끝까지 포함한다.
const dayToIso = (day, endOfDay) => {
  if (!day) return undefined;
  const d = new Date(`${day}T${endOfDay ? "23:59:59.999" : "00:00:00"}`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

const json = (v) => (v === null || v === undefined ? "-" : JSON.stringify(v, null, 2));

/** 권한 관리 — 변경 이력 (spec R-MGMT-09, R-FE-06) */
function AccessAuditLogs() {
  const { admins } = useAdminList();
  const adminById = useMemo(() => new Map(admins.map((a) => [a.adminUserId, a])), [admins]);

  const [tempFilter, setTempFilter] = useState(EMPTY_FILTER);
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAuditLogs({
        targetType: filter.targetType,
        targetId: filter.targetId.trim(),
        actorAdminUserId: filter.actorAdminUserId,
        from: dayToIso(filter.from, false),
        to: dayToIso(filter.to, true),
        page,
        size: PAGE_SIZE,
      });
      setResult({ items: data?.items ?? [], total: data?.total ?? 0 });
    } catch (err) {
      setResult({ items: [], total: 0 });
      setError(getAccessErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    load();
  }, [load]);

  const change = (e) => setTempFilter((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const search = () => {
    setPage(1);
    setFilter(tempFilter);
  };
  const reset = () => {
    setTempFilter(EMPTY_FILTER);
    setFilter(EMPTY_FILTER);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(result.total / PAGE_SIZE));
  const actorName = (id) => {
    const a = adminById.get(id);
    return a ? `${a.name || "(이름 없음)"} #${id}` : `#${id}`;
  };

  return (
    <Layout>
      <div className="access-page">
        <div>
          <h4 className="access-page-title">권한 관리 · 변경 이력</h4>
          <p className="access-page-desc">역할·개인 예외·역할 권한 변경은 모두 사유와 함께 기록됩니다(삭제 불가).</p>
        </div>

        <div className="access-section access-toolbar">
          <select name="targetType" className="form-select form-select-sm" value={tempFilter.targetType} onChange={change}>
            <option value="">대상 전체</option>
            <option value="USER">어드민</option>
            <option value="ROLE">역할</option>
          </select>
          <input
            name="targetId"
            className="form-control form-control-sm"
            placeholder="대상 ID (어드민 번호·역할 키)"
            value={tempFilter.targetId}
            onChange={change}
            onKeyDown={(e) => e.key === "Enter" && search()}
          />
          <select
            name="actorAdminUserId"
            className="form-select form-select-sm"
            value={tempFilter.actorAdminUserId}
            onChange={change}
          >
            <option value="">변경자 전체</option>
            {admins.map((a) => (
              <option key={a.adminUserId} value={a.adminUserId}>
                {adminLabel(a)}
              </option>
            ))}
          </select>
          <input name="from" type="date" className="form-control form-control-sm" value={tempFilter.from} onChange={change} />
          <span className="access-muted">~</span>
          <input name="to" type="date" className="form-control form-control-sm" value={tempFilter.to} onChange={change} />
          <button type="button" className="btn btn-primary btn-sm" onClick={search}>
            검색
          </button>
          <button type="button" className="btn btn-light btn-sm" onClick={reset}>
            초기화
          </button>
        </div>

        {error && <div className="access-error">{error}</div>}

        <div className="access-section access-table-wrap">
          {loading ? (
            <div className="access-empty">불러오는 중...</div>
          ) : result.items.length === 0 ? (
            <div className="access-empty">조회된 이력이 없습니다</div>
          ) : (
            <table className="table table-hover access-table">
              <thead>
                <tr>
                  <th>시각</th>
                  <th>변경자</th>
                  <th>대상</th>
                  <th>동작</th>
                  <th>사유</th>
                  <th>변경 내용</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((log) => {
                  const self = log.targetType === "USER" && String(log.actorAdminUserId) === String(log.targetId);
                  return (
                    <tr key={log.id}>
                      <td style={{ whiteSpace: "nowrap" }}>{formatDateTime(log.createdAt)}</td>
                      <td>{actorName(log.actorAdminUserId)}</td>
                      <td>
                        {log.targetType === "USER" ? actorName(Number(log.targetId)) : `역할 ${log.targetId}`}
                        {self && <span className="access-badge access-badge-expired ms-1">본인 변경</span>}
                      </td>
                      <td>{ACTION_LABEL[log.action] ?? log.action}</td>
                      <td>{log.reason}</td>
                      <td>
                        <details>
                          <summary className="access-muted">전/후 보기</summary>
                          <div className="access-muted">변경 전</div>
                          <pre className="access-json">{json(log.before)}</pre>
                          <div className="access-muted">변경 후</div>
                          <pre className="access-json">{json(log.after)}</pre>
                        </details>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div className="access-pagination">
            <span>
              총 {result.total}건 · {page}/{totalPages}
            </span>
            <button type="button" className="btn btn-light btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              이전
            </button>
            <button
              type="button"
              className="btn btn-light btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              다음
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default AccessAuditLogs;
